import {
  codeDefiValide,
  libellesConnexions,
  type ReportDemande,
  type ToucheVue,
  type TypeDefi,
} from '@admin-pmvb/core'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useRouterState } from '@tanstack/react-router'
import type { InferResponseType } from 'hono/client'
import { type ReactNode, useEffect, useRef, useState } from 'react'
import { api, ErreurApi, lire, requetes } from '../api'
import { Decompte } from '../composants/Decompte'
import { Icone } from '../composants/Icone'
import { textesDefiLocal } from '../defi-local'
import { ActiverNotifications } from '../composants/Notifications'

// Écran d'un défi (ce que le portail demande à un humain), plein écran, pensé pour le téléphone :
// ouvert par la notification (/defi/<id>) ou par-dessus la page en cours quand un défi en direct
// attend le titulaire. Un code : un seul champ, que le clavier de l'iPhone propose de remplir
// depuis le SMS reçu ; une validation dans l'appli du fournisseur : un compte à rebours, l'écran
// suit seul ; une connexion à relancer : un bouton. Toujours « Pas maintenant », et l'issue.

type Defi = InferResponseType<(typeof api.portails.defis)[':id']['$get'], 200>
type Disponibilite = NonNullable<
  InferResponseType<typeof api.portails.disponibilite.$get, 200>['demande']
>

/** Codes que le fournisseur envoie en chiffres : clavier numérique. */
const NUMERIQUES: readonly TypeDefi[] = ['otp_sms', 'totp']
const TERMINEES = new Set(['terminee', 'expiree', 'abandonnee', 'refusee', 'echec'])

export function EcranDefi({
  id,
  onFermer,
  onSuivant,
}: {
  id: string
  /** Retour à l'endroit d'où l'on venait. */
  onFermer: () => void
  /** La collecte a ouvert la vérification suivante (après « Lancer la connexion »). */
  onSuivant: (id: string) => void
}) {
  const client = useQueryClient()
  const requete = useQuery({
    ...requetes.defi(id),
    // L'écran suit la collecte tant qu'elle avance ; rien en arrière-plan.
    refetchInterval: (q) =>
      q.state.data && TERMINEES.has(q.state.data.issue.etape) && !q.state.data.suivantId
        ? false
        : 2000,
    retry: false,
  })
  const defi = requete.data
  const [erreur, setErreur] = useState<string | null>(null)
  const [occupe, setOccupe] = useState(false)

  // Une seule fois par défi suivant, même si l'écran se redessine avant de basculer.
  const suivantVu = useRef<string | null>(null)
  useEffect(() => {
    if (!defi?.suivantId || suivantVu.current === defi.suivantId) return
    suivantVu.current = defi.suivantId
    onSuivant(defi.suivantId)
  }, [defi?.suivantId, onSuivant])

  const agir = async (appel: () => Promise<unknown>) => {
    setErreur(null)
    setOccupe(true)
    try {
      await appel()
    } catch (x) {
      setErreur(x instanceof ErreurApi ? x.message : 'Action impossible.')
    } finally {
      setOccupe(false)
      await client.invalidateQueries({ queryKey: ['portails'] })
    }
  }

  return (
    <div
      className="defi-ecran"
      role="dialog"
      aria-modal="true"
      aria-labelledby="defi-titre"
      data-testid="ecran-defi"
    >
      <header className="defi-entete">
        <span className="defi-marque">
          <Icone nom="verified" taille={22} />
        </span>
        <div>
          <p className="defi-surtitre">Vérification demandée</p>
          <h1 id="defi-titre">{defi?.connexion.libelle ?? 'Portail'}</h1>
        </div>
        <button
          type="button"
          className="bouton-icone"
          onClick={onFermer}
          aria-label="Fermer"
          title="Fermer"
        >
          <Icone nom="close" />
        </button>
      </header>
      <div className="defi-corps">
        {requete.isPending && <p className="discret">Chargement…</p>}
        {requete.isError && (
          <Issue texte="Cette vérification n’existe pas ou plus." onFermer={onFermer} />
        )}
        {defi && <Contenu defi={defi} occupe={occupe} agir={agir} onFermer={onFermer} />}
        {erreur && <p className="alerte">{erreur}</p>}
      </div>
    </div>
  )
}

function Contenu({
  defi,
  occupe,
  agir,
  onFermer,
}: {
  defi: Defi
  occupe: boolean
  agir: (appel: () => Promise<unknown>) => Promise<void>
  onFermer: () => void
}) {
  const param = { param: { id: defi.id } }
  const pasMaintenant = (
    <button
      type="button"
      className="bouton-discret defi-pas-maintenant"
      disabled={occupe}
      onClick={() => agir(() => lire(api.portails.defis[':id'].annuler.$post(param)))}
    >
      Pas maintenant
    </button>
  )
  const { etape, texte } = defi.issue

  if (TERMINEES.has(etape))
    return (
      <Issue
        texte={texte}
        detail={etape === 'refusee' || etape === 'echec' ? defi.collecte.erreur : null}
        reussie={etape === 'terminee'}
        onFermer={onFermer}
      />
    )

  if (!defi.titulaire)
    return (
      <>
        <p className="defi-demande">
          {defi.titulaireNom ?? 'Le titulaire'} doit répondre : {defi.libelle}.
        </p>
        <p className="discret">{texte}</p>
        <button type="button" className="bouton" onClick={onFermer}>
          Retour
        </button>
      </>
    )

  if (etape === 'connexion' || etape === 'collecte')
    return (
      <>
        <p className="defi-demande" role="status">
          <span className="defi-attente" aria-hidden="true" />
          {texte}
        </p>
        <button type="button" className="bouton-discret" onClick={onFermer}>
          Fermer, la collecte continue
        </button>
      </>
    )

  // Collecteur local : la fenêtre (ou, pour un portail de justificatifs, l'onglet prêté de Chrome)
  // s'ouvre sur l'ordinateur du titulaire, il y fait tout lui-même ; rien ne se saisit ici. La
  // consigne de l'agent passe d'abord (extension à relier, passage d'une autre personne).
  if (defi.conduite.mode === 'local') {
    const ou = defi.ordinateur ?? 'votre ordinateur'
    const textes = textesDefiLocal({
      message: defi.message,
      libelle: defi.libelle,
      ongletPrete: defi.connexion.ongletPrete,
      ou,
    })
    return defi.enDirect ? (
      <>
        <p className="defi-demande defi-grand">Connectez-vous sur {ou}</p>
        <p className="discret">
          {textes.ouvert} ({defi.libelle}). Faites l’étape vous-même ; le robot reprend dès que le
          portail vous voit connecté, cet écran avance seul.
        </p>
        <p className="defi-decompte">
          <span className="defi-attente" aria-hidden="true" />
          <Decompte jusqua={defi.expireLe} className="" />
        </p>
        {pasMaintenant}
      </>
    ) : (
      <>
        <p className="defi-demande">{textes.attente}</p>
        <button
          type="button"
          className="bouton bouton-principal defi-action"
          disabled={occupe}
          onClick={() => agir(() => lire(api.portails.defis[':id'].continuer.$post(param)))}
        >
          C’est parti
        </button>
        {pasMaintenant}
      </>
    )
  }

  // En attente d'une réponse.
  if (!defi.enDirect)
    return (
      <>
        <p className="defi-demande">
          Le portail demandera une vérification ({defi.libelle}). Lancez la connexion quand vous
          pouvez y répondre : elle arrive dans la minute.
        </p>
        <button
          type="button"
          className="bouton bouton-principal defi-action"
          disabled={occupe}
          onClick={() => agir(() => lire(api.portails.defis[':id'].continuer.$post(param)))}
        >
          Lancer la connexion maintenant
        </button>
        {pasMaintenant}
        <ActiverNotifications compact />
      </>
    )

  if (defi.conduite.mode === 'code')
    return <SaisieCode defi={defi} occupe={occupe} agir={agir} pasMaintenant={pasMaintenant} />

  if (defi.conduite.mode === 'vue')
    return defi.vue.active ? (
      <VueEnDirect defi={defi} pasMaintenant={pasMaintenant} />
    ) : (
      <>
        <p className="defi-demande">
          Le portail demande : {defi.libelle}. La vue en direct est coupée sur ce serveur.
        </p>
        {pasMaintenant}
      </>
    )

  if (defi.conduite.mode === 'attente')
    return (
      <>
        <p className="defi-demande defi-grand">Validez sur votre téléphone</p>
        <p className="discret">
          {defi.message ?? `Le portail attend une ${defi.libelle}.`} Cet écran avance seul dès la
          validation.
        </p>
        <p className="defi-decompte">
          <span className="defi-attente" aria-hidden="true" />
          <Decompte jusqua={defi.expireLe} className="" />
        </p>
        {pasMaintenant}
        <ActiverNotifications compact />
      </>
    )

  return (
    <>
      <p className="defi-demande">
        Le portail demande : {defi.libelle}. Le serveur ne peut pas le faire à votre place :
        connectez-vous vous-même, puis relancez la collecte.
      </p>
      {pasMaintenant}
    </>
  )
}

const TOUCHES: { touche: ToucheVue; libelle: string }[] = [
  { touche: 'Enter', libelle: 'Entrée' },
  { touche: 'Backspace', libelle: 'Effacer' },
  { touche: 'Tab', libelle: 'Tab' },
  { touche: 'ArrowLeft', libelle: '←' },
  { touche: 'ArrowRight', libelle: '→' },
  { touche: 'ArrowUp', libelle: '↑' },
  { touche: 'ArrowDown', libelle: '↓' },
]
const ZOOMS = [1, 1.5, 2, 3] as const

/**
 * Vue en direct du navigateur du robot : l'écran du Chromium du collecteur, que le titulaire
 * touche pour franchir lui-même le CAPTCHA ou les conditions. Images en mémoire seulement (URL
 * d'objet révoquée à chaque image) ; rien n'est gardé. Le robot reprend la main seul dès que le
 * portail le voit connecté : l'écran passe alors à l'issue.
 */
function VueEnDirect({ defi, pasMaintenant }: { defi: Defi; pasMaintenant: ReactNode }) {
  const [image, setImage] = useState<string | null>(null)
  const [etat, setEtat] = useState<'attente' | 'ouverte' | 'fermee'>('attente')
  const [erreur, setErreur] = useState<string | null>(null)
  const [zoom, setZoom] = useState<(typeof ZOOMS)[number]>(1)
  const [texte, setTexte] = useState('')
  // Ouverte sur un geste : un autre appareil ouvert sur l'appli ne prend pas la vue de lui-même.
  const [essai, setEssai] = useState(0)
  const socket = useRef<WebSocket | null>(null)
  const derniere = useRef<string | null>(null)
  const prete = defi.vue.prete

  useEffect(() => {
    if (!prete || socket.current || essai === 0) return
    let annule = false
    void lire(api.portails.defis[':id'].vue.$post({ param: { id: defi.id } }))
      .then(({ jeton }) => {
        if (annule) return
        const proto = location.protocol === 'https:' ? 'wss' : 'ws'
        const ws = new WebSocket(
          `${proto}://${location.host}/api/portails/defis/${defi.id}/vue?jeton=${encodeURIComponent(jeton)}`,
        )
        ws.binaryType = 'blob'
        socket.current = ws
        ws.onopen = () => setEtat('ouverte')
        ws.onmessage = (e: MessageEvent) => {
          if (e.data instanceof Blob) {
            const adresse = URL.createObjectURL(e.data)
            if (derniere.current) URL.revokeObjectURL(derniere.current)
            derniere.current = adresse
            setImage(adresse)
          }
        }
        ws.onclose = () => {
          socket.current = null
          setEtat('fermee')
        }
      })
      .catch((x: unknown) => setErreur(x instanceof ErreurApi ? x.message : 'Vue indisponible.'))
    return () => {
      annule = true
    }
  }, [prete, defi.id, essai])

  // En quittant l'écran : la vue se ferme, l'image s'oublie.
  useEffect(
    () => () => {
      socket.current?.close()
      if (derniere.current) URL.revokeObjectURL(derniere.current)
    },
    [],
  )

  const envoyer = (m: object) => {
    if (socket.current?.readyState === WebSocket.OPEN) socket.current.send(JSON.stringify(m))
  }
  const geste = (g: object) => envoyer({ type: 'geste', geste: g })
  const position = (e: { clientX: number; clientY: number }, cible: HTMLElement) => {
    const r = cible.getBoundingClientRect()
    const borne = (v: number) => Math.min(1, Math.max(0, v))
    return { x: borne((e.clientX - r.left) / r.width), y: borne((e.clientY - r.top) / r.height) }
  }
  const borneDefilement = (v: number) => Math.max(-2000, Math.min(2000, Math.round(v)))

  if (essai === 0 || (!prete && etat === 'attente'))
    return (
      <>
        <p className="defi-demande" role="status">
          {!prete && <span className="defi-attente" aria-hidden="true" />}
          {prete
            ? `Le portail demande : ${defi.libelle}. Franchissez l’étape vous-même, en voyant et en touchant le navigateur du robot.`
            : `Le portail demande : ${defi.libelle}. Le navigateur du robot se prépare…`}
        </p>
        {prete && defi.vue.occupee && (
          <p className="discret">La vue est déjà ouverte sur un autre appareil.</p>
        )}
        {prete && (
          <button
            type="button"
            className="bouton bouton-principal defi-action"
            disabled={defi.vue.occupee}
            onClick={() => setEssai((n) => n + 1)}
          >
            <Icone nom="touch_app" /> Ouvrir la vue en direct
          </button>
        )}
        {erreur && <p className="alerte">{erreur}</p>}
        {pasMaintenant}
      </>
    )

  return (
    <div className="vue-direct">
      <div className="vue-bandeau" role="status">
        <Icone nom="touch_app" />
        <span>Vous pilotez le navigateur du robot sur {defi.connexion.libelle}</span>
        <button
          type="button"
          className="bouton-discret"
          onClick={() => envoyer({ type: 'terminer' })}
          disabled={etat !== 'ouverte'}
        >
          Terminer
        </button>
      </div>
      <p className="discret">
        {defi.libelle} : touchez l’écran ci-dessous comme sur le site. Vos identifiants sont déjà
        remplis ; le robot reprend dès que le portail vous voit connecté.
      </p>
      <div className="vue-cadre" data-testid="vue-cadre">
        {image ? (
          <img
            src={image}
            alt={`Écran du navigateur du robot sur ${defi.connexion.libelle}`}
            className="vue-image"
            style={{ width: `${zoom * 100}%` }}
            draggable={false}
            onClick={(e) => geste({ type: 'clic', ...position(e, e.currentTarget) })}
            onWheel={(e) =>
              geste({
                type: 'molette',
                ...position(e, e.currentTarget),
                dx: borneDefilement(e.deltaX),
                dy: borneDefilement(e.deltaY),
              })
            }
          />
        ) : (
          <p className="discret vue-vide">
            {etat === 'fermee' ? 'Vue fermée.' : 'En attente de l’image…'}
          </p>
        )}
      </div>
      <div className="vue-commandes">
        <button
          type="button"
          className="bouton-discret"
          aria-label="Réduire"
          disabled={zoom === ZOOMS[0]}
          onClick={() => setZoom(ZOOMS[Math.max(0, ZOOMS.indexOf(zoom) - 1)]!)}
        >
          −
        </button>
        <button
          type="button"
          className="bouton-discret"
          aria-label="Agrandir"
          disabled={zoom === ZOOMS[ZOOMS.length - 1]}
          onClick={() => setZoom(ZOOMS[Math.min(ZOOMS.length - 1, ZOOMS.indexOf(zoom) + 1)]!)}
        >
          +
        </button>
        <button
          type="button"
          className="bouton-discret"
          aria-label="Faire défiler vers le haut"
          onClick={() => geste({ type: 'molette', x: 0.5, y: 0.5, dx: 0, dy: -400 })}
        >
          Monter
        </button>
        <button
          type="button"
          className="bouton-discret"
          aria-label="Faire défiler vers le bas"
          onClick={() => geste({ type: 'molette', x: 0.5, y: 0.5, dx: 0, dy: 400 })}
        >
          Descendre
        </button>
      </div>
      <form
        className="vue-texte"
        onSubmit={(e) => {
          e.preventDefault()
          // eslint-disable-next-line no-control-regex
          const propre = texte.replace(/[\u0000-\u001f\u007f]/g, '').slice(0, 200)
          if (propre) geste({ type: 'texte', texte: propre })
          setTexte('')
        }}
      >
        <label htmlFor="vue-saisie" className="discret">
          Texte à écrire dans le champ touché
        </label>
        <div className="vue-saisie">
          <input
            id="vue-saisie"
            value={texte}
            onChange={(e) => setTexte(e.target.value)}
            autoComplete="off"
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
            maxLength={200}
          />
          <button type="submit" className="bouton">
            Écrire
          </button>
        </div>
      </form>
      <div className="vue-touches">
        {TOUCHES.map((t) => (
          <button
            key={t.touche}
            type="button"
            className="bouton-discret"
            onClick={() => geste({ type: 'touche', touche: t.touche })}
          >
            {t.libelle}
          </button>
        ))}
      </div>
      {etat === 'fermee' && defi.statut === 'en_attente' && (
        <button
          type="button"
          className="bouton"
          onClick={() => {
            setEtat('attente')
            setErreur(null)
            setEssai((n) => n + 1)
          }}
        >
          Reprendre la main
        </button>
      )}
      {erreur && <p className="alerte">{erreur}</p>}
      {pasMaintenant}
    </div>
  )
}

function SaisieCode({
  defi,
  occupe,
  agir,
  pasMaintenant,
}: {
  defi: Defi
  occupe: boolean
  agir: (appel: () => Promise<unknown>) => Promise<void>
  pasMaintenant: ReactNode
}) {
  const [code, setCode] = useState('')
  const [invalide, setInvalide] = useState(false)
  const champ = useRef<HTMLInputElement>(null)
  useEffect(() => champ.current?.focus(), [])
  const numerique = NUMERIQUES.includes(defi.type)
  return (
    <form
      className="defi-formulaire"
      onSubmit={(e) => {
        e.preventDefault()
        const propre = codeDefiValide(code)
        setInvalide(!propre)
        if (propre)
          void agir(() =>
            lire(
              api.portails.defis[':id'].code.$post({
                param: { id: defi.id },
                json: { code: propre },
              }),
            ),
          )
      }}
    >
      <label htmlFor="defi-code" className="defi-demande">
        {defi.message ?? defi.libelle}
      </label>
      <input
        id="defi-code"
        ref={champ}
        className="defi-code"
        value={code}
        onChange={(e) => setCode(e.target.value)}
        autoComplete="one-time-code"
        inputMode={numerique ? 'numeric' : 'text'}
        {...(numerique && { pattern: '[0-9 ]*' })}
        autoCapitalize="off"
        autoCorrect="off"
        spellCheck={false}
        maxLength={20}
        aria-invalid={invalide}
        aria-describedby="defi-limite"
        required
      />
      {invalide && <p className="alerte">Code illisible : 4 à 12 chiffres ou lettres.</p>}
      <p id="defi-limite" className="defi-decompte">
        <Decompte jusqua={defi.expireLe} className="" />
      </p>
      <button type="submit" className="bouton bouton-principal defi-action" disabled={occupe}>
        Valider
      </button>
      {pasMaintenant}
      <ActiverNotifications compact />
    </form>
  )
}

function Issue({
  texte,
  detail = null,
  reussie = false,
  onFermer,
}: {
  texte: string
  detail?: string | null
  reussie?: boolean
  onFermer: () => void
}) {
  return (
    <>
      <p className="defi-demande defi-issue" role="status">
        <Icone nom={reussie ? 'check_circle' : 'warning'} taille={28} />
        {texte}
      </p>
      {detail && <p className="discret">{detail}</p>}
      <button type="button" className="bouton bouton-principal defi-action" onClick={onFermer}>
        Retour
      </button>
    </>
  )
}

/**
 * Demande de disponibilité (décision du 02/10/2026) : des connexions attendent leur titulaire
 * pour se reconnecter. « C'est parti » les relance (les vérifications arrivent ensuite, chacune
 * avec son écran) ; « Dans 1 h » et « Demain » reportent la question. L'issue reste affichée
 * jusqu'à « Retour ».
 */
export function EcranDisponibilite({
  demande,
  onFermer,
}: {
  demande: Disponibilite
  onFermer: () => void
}) {
  const client = useQueryClient()
  const [erreur, setErreur] = useState<string | null>(null)
  const [occupe, setOccupe] = useState(false)
  const [issue, setIssue] = useState<string | null>(null)
  const param = { param: { id: demande.id } }
  const plusieurs = demande.connexions.length > 1

  const agir = async (appel: () => Promise<unknown>, texte: string) => {
    setErreur(null)
    setOccupe(true)
    try {
      await appel()
      setIssue(texte)
    } catch (x) {
      setErreur(x instanceof ErreurApi ? x.message : 'Action impossible.')
    } finally {
      setOccupe(false)
      await client.invalidateQueries({ queryKey: ['portails'] })
    }
  }
  const reporter = (quand: ReportDemande, texte: string) =>
    agir(
      () => lire(api.portails.disponibilite[':id'].reporter.$post({ ...param, json: { quand } })),
      texte,
    )

  return (
    <div
      className="defi-ecran"
      role="dialog"
      aria-modal="true"
      aria-labelledby="disponibilite-titre"
      data-testid="ecran-disponibilite"
    >
      <header className="defi-entete">
        <span className="defi-marque">
          <Icone nom="front_hand" taille={22} />
        </span>
        <div>
          <p className="defi-surtitre">Pièces à récupérer</p>
          <h1 id="disponibilite-titre">Êtes-vous disponible ?</h1>
        </div>
        <button
          type="button"
          className="bouton-icone"
          onClick={onFermer}
          aria-label="Fermer"
          title="Fermer"
        >
          <Icone nom="close" />
        </button>
      </header>
      <div className="defi-corps">
        {issue ? (
          <Issue texte={issue} reussie onFermer={onFermer} />
        ) : (
          <>
            <p className="defi-demande">
              {libellesConnexions(demande.connexions)} {plusieurs ? 'attendent' : 'attend'} votre
              présence pour {plusieurs ? 'leur' : 'sa'} relève : un code ou une vérification va vous
              être demandé dans les minutes qui suivent.
            </p>
            <p className="defi-decompte">
              <Decompte jusqua={demande.expireLe} className="" />
            </p>
            <button
              type="button"
              className="bouton bouton-principal defi-action"
              disabled={occupe}
              onClick={() =>
                agir(
                  () => lire(api.portails.disponibilite[':id'].accepter.$post(param)),
                  'C’est parti : les vérifications arrivent dans les minutes qui suivent, chacune avec son écran.',
                )
              }
            >
              C’est parti
            </button>
            <button
              type="button"
              className="bouton defi-action"
              disabled={occupe}
              onClick={() => reporter('une_heure', 'Entendu : je vous redemande dans une heure.')}
            >
              Dans 1 h
            </button>
            <button
              type="button"
              className="bouton-discret defi-pas-maintenant"
              disabled={occupe}
              onClick={() => reporter('demain', 'Entendu : je vous redemande demain à 9 h.')}
            >
              Demain
            </button>
            <ActiverNotifications compact />
          </>
        )}
        {erreur && <p className="alerte">{erreur}</p>}
      </div>
    </div>
  )
}

/**
 * Par-dessus l'appli déjà ouverte : dès qu'un défi en direct attend la personne connectée, son
 * écran s'affiche, où qu'elle soit ; sinon, sa demande de disponibilité. Relu toutes les cinq
 * secondes quand la page est visible (rien en arrière-plan : la notification prend le relais).
 */
export function SurimpressionDefis() {
  const client = useQueryClient()
  const chemin = useRouterState({ select: (s) => s.location.pathname })
  const [actif, setActif] = useState<string | null>(null)
  const [ecartes, setEcartes] = useState<ReadonlySet<string>>(() => new Set())
  const [disponibilite, setDisponibilite] = useState<Disponibilite | null>(null)
  const [demandesVues, setDemandesVues] = useState<ReadonlySet<string>>(() => new Set())
  const releve = { refetchInterval: 5000, refetchIntervalInBackground: false } as const
  const mesDefis = useQuery({ ...requetes.mesDefis, ...releve })
  const maDisponibilite = useQuery({ ...requetes.maDisponibilite, ...releve })
  const surEcranDefi = chemin.startsWith('/defi/')
  const enAttente = (mesDefis.data ?? []).find((d) => d.enDirect && !ecartes.has(d.id))
  const demande = maDisponibilite.data?.demande ?? null

  useEffect(() => {
    if (!actif && enAttente && !surEcranDefi) setActif(enAttente.id)
  }, [actif, enAttente, surEcranDefi])
  useEffect(() => {
    if (!disponibilite && demande && !demandesVues.has(demande.id)) setDisponibilite(demande)
  }, [disponibilite, demande, demandesVues])

  if (surEcranDefi) return null
  if (actif)
    return (
      <EcranDefi
        id={actif}
        onFermer={() => {
          setEcartes((e) => new Set([...e, actif]))
          setActif(null)
        }}
        onSuivant={(suivant) => {
          setEcartes((e) => new Set([...e, actif]))
          setActif(suivant)
        }}
      />
    )
  if (disponibilite)
    return (
      <EcranDisponibilite
        demande={disponibilite}
        onFermer={() => {
          setDemandesVues((v) => new Set([...v, disponibilite.id]))
          setDisponibilite(null)
          // Après « C'est parti », la vérification qui arrive s'affiche sans attendre la relecture.
          void client.invalidateQueries({ queryKey: requetes.mesDefis.queryKey })
        }}
      />
    )
  return null
}
